/**
 * S2 version list: every blueprint revision of an agent, newest first.
 * Each row summarises lifecycle, decision mode, and declared compute classes.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { LifecycleBadge } from './LifecycleBadge';
import type { AiStudioAgentVersion } from '@/types/aiStudioAgent';

interface VersionListProps {
  versions: AiStudioAgentVersion[];
  onSelect: (versionId: string) => void;
}

export const VersionList: React.FC<VersionListProps> = ({ versions, onSelect }) => {
  const { t } = useLanguage();

  if (versions.length === 0) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.agents.detail.noVersions')}
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {versions.map((version) => (
        <li key={version.id}>
          <button
            type="button"
            onClick={() => onSelect(version.id)}
            className="flex w-full flex-wrap items-center gap-3 rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-3 py-2 text-left hover:bg-[var(--color-surface-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          >
            <span className="font-medium text-[var(--color-surface-text)]">
              {t('aiStudio.versions.title', { number: version.versionNumber })}
            </span>
            <LifecycleBadge status={version.lifecycleStatus} />
            <span className="text-base text-[var(--color-surface-text-secondary)]">
              {t(
                `aiStudio.definition.defaultDecisionMode.${version.defaultDecisionMode ?? 'PROVISIONAL'}`
              )}
            </span>
            <span className="text-base text-[var(--color-surface-text-secondary)]">
              {version.computeClasses
                .map((computeClass) => t(`aiStudio.versions.computeClass.${computeClass}`))
                .join(', ')}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};
